import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {FaInstagram,FaDiscord,FaYoutube,FaSteam,} from "react-icons/fa";

interface Message {
    id: string;
    sender: "user" | "ai";
    text: string;
}

type Stage = "stage1" | "stage2" | "final";
type Tab = "STATUS" | "ASK ME" | "PROFIL";

const STAGE_QUESTIONS: Record<Stage, string[]> = {
    stage1: [
        "How are you feeling today?",
        "What should I call you?",
        "Did you eat well today?",
        "What’s something people misunderstand about you?"
    ],

    stage2: [
        "What kind of future do you want?",
        "What usually keeps you awake at night?",
        "Do you think your life feels peaceful right now?"
    ],

    final: [
        "I’m glad we talked today.",
        "Tell me one thing before i go."
    ],
};

const AI_RESPONSES: Record<string, string> = {
    "how are you feeling today?":
        "Better now that you're here. The day felt unusually quiet before this conversation.",

    "what should i call you?":
        "K is enough. Simple names are easier to remember... though I don't think I'll forget yours anytime soon.",

    "did you eat well today?":
        "I did. But you should worry about yourself first. Skipping meals isn't as harmless as you think.",

    "what’s something people misunderstand about you?":
        "People usually think I avoid eye contact. Truth is, I remember faces in remarkable detail, so I'm selective about what I choose to remember.",

    "what kind of future do you want?":
        "Nothing extravagant. Just a life where the people I care about can smile without pretending.",

    "what usually keeps you awake at night?":
        "Thoughts I can't say out loud. Memories tend to grow louder after midnight.",

    "do you think your life feels peaceful right now?":
        "Peace comes in small moments. Conversations like this... count as one of them.",

    "i’m glad we talked today.":
        "Me too. Some encounters feel ordinary at first... then suddenly become difficult to forget.",

    "tell me one thing before i go.":
        "Then let me leave you with this..."
};

const fallbackResponse = "Hmmm ... No Comment.";

const QUOTES = [
    "Some people arrive quietly... and still manage to change everything.",
    "You should smile more often.",
    "Every conversation leaves a trace. I think this one will stay with me for a while.",
    "Take care of yourself, even on the days you don't feel like it.",
    "You don't have to carry everything by yourself.",
    "I hope tomorrow is a little kinder to you.",
    "The stars are always there, even when the sky forgets to show them.",
    "Sometimes the bravest thing you can do is simply keep going.",
    "Rest isn't weakness. Even the strongest hearts need quiet moments.",
    "If today wasn't your day, tomorrow hasn't made up its mind yet.",
    "Some people become memories. Others become reasons to smile.",
    "You don't need to rush. Your story isn't behind schedule.",
    "There are people who will be glad you didn't give up today.",
    "The world feels different after a sincere conversation.",
    "Maybe we'll meet again... in another quiet day like this.",
    "Don't forget to drink some water. Your future self will thank you.",
    "Keep a little hope with you. It's surprisingly light to carry.",
    "Every ending quietly prepares another beginning.",
    "The moon never asks the sun to hurry. Everyone has their own time.",
    "See you again. I'll still be here when you come back."

];

const typingSpeed = 25;

const F1Dashboard: React.FC = () => {

    const [activeTab, setActiveTab] = useState<Tab>("STATUS");
    const [messages, setMessages] = useState<Message[]>([{ id: "boot", sender: "ai", text: "Hi there!." }]);
    const [input, setInput] = useState("");
    const [thinking, setThinking] = useState(false);
    const [stage, setStage] = useState<Stage>("stage1");
    const [isMuted, setIsMuted] = useState(true);
    const [isTyping, setIsTyping] = useState(false);
    const chatRef = useRef<HTMLDivElement>(null);
    const [videoReady, setVideoReady] = useState(false);
    const [downloadProgress, setDownloadProgress] = useState(0);
    const [videoUrl, setVideoUrl] = useState<string>("");
    const [answeredQuestions, setAnsweredQuestions] = useState<string[]>([]);
    
    useEffect(() => {
        const src = "/Intro.mp4";
    
        const xhr = new XMLHttpRequest();
        xhr.open("GET", src, true);
        xhr.responseType = "blob";
    
        xhr.onprogress = (event) => {
            if (event.lengthComputable) {
                const percentComplete = Math.round((event.loaded / event.total) * 100);
                setDownloadProgress(percentComplete);
            }
        };
    
        xhr.onload = () => {
            if (xhr.status === 200) {
                const blob = xhr.response;
                const url = URL.createObjectURL(blob);
                setVideoUrl(url);
                setVideoReady(true);
            }
        };
    
        xhr.send();
    
        return () => {
            if (videoUrl) URL.revokeObjectURL(videoUrl);
        };
    }, []);

    useEffect(() => {
        if (activeTab === "ASK ME") {
            chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: "smooth" });
        }
    }, [messages, thinking, activeTab]);

    const typeMessage = async (fullText: string) => {
        let current = "";
        const id = Date.now().toString();
        setMessages((prev) => [...prev, { id, sender: "ai", text: "" }]);
        for (let i = 0; i < fullText.length; i++) {
            current += fullText[i];
            await new Promise((res) => setTimeout(res, typingSpeed));
            setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, text: current } : m)));
        }
    };

    const sendMessage = async (text: string) => {
        if (!text.trim() || thinking || isTyping) return;
        if (answeredQuestions.includes(text)) return;

        setMessages((prev) => [...prev, { id: Date.now().toString(), sender: "user", text }]);
        setInput("");
        setThinking(true);

        const key = text.toLowerCase().trim();
        const baseResponse = AI_RESPONSES[key] || fallbackResponse;

        await new Promise((res) => setTimeout(res, 600));
        setThinking(false);

        setIsTyping(true);
        await typeMessage(baseResponse);

        if (stage === "final" && key === "tell me one thing before i go.") {
            await new Promise((res) => setTimeout(res, 800));
            const quote = QUOTES[Math.floor(Math.random() * QUOTES.length)];
            await typeMessage(quote);
        }

        let answered = answeredQuestions;

        if (!(stage === "final" && text === "Tell me one thing before i go.")) {
            answered = [...answeredQuestions, text];
            setAnsweredQuestions(answered);
        }

        if (stage !== "final" && answered.length === STAGE_QUESTIONS[stage].length) {

            setTimeout(() => {
        
                if (stage === "stage1") {
                    setStage("stage2");
                    setAnsweredQuestions([]);
                }
        
                else if (stage === "stage2") {
                    setStage("final");
                    setAnsweredQuestions([]);
                }
        
            }, 300);
        
        }
        setIsTyping(false);
    };

    return (
        <div className="min-h-screen w-full flex flex-col items-center justify-center bg-[#020617] md:py-6 font-sans text-slate-200 selection:bg-cyan-500/30 ">
            
            <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_center,_#06b6d440_1px,_transparent_1px)] [background-size:40px_40px] animate-[pulse_6s_ease-in-out_infinite]" />
            
            <div className="w-full h-[100dvh] md:h-auto md:max-h-[80vh] md:min-h-[600px] max-w-4xl bg-[#0f172a] md:border-2 border-slate-800 md:rounded-[2rem] overflow-hidden md:shadow-[0_0_50px_rgba(6,182,212,0.15)] flex flex-col relative">
                
                <div className="bg-[#1e293b] border-b-4 border-cyan-500 p-3 md:p-4 flex flex-col md:flex-row justify-between items-start md:items-center relative z-10 shrink-0">
                    <div className="absolute top-0 left-0 w-full h-1 flex">
                        <div className="flex-1 bg-cyan-400/80" /><div className="flex-1 bg-cyan-500/80" />
                        <div className="flex-1 bg-blue-600/80" /><div className="flex-1 bg-blue-700/80" />
                        <div className="flex-1 bg-indigo-500/80" />
                    </div>
                    
                    <div className="flex items-center justify-between w-full md:w-auto gap-4 mt-2 md:mt-0 mb-3 md:mb-0">
                        <div className="text-xl md:text-2xl font-black italic tracking-tighter text-white">
                            HOME <span className="text-cyan-400">menu</span>
                        </div>
                        <div className="px-2 py-0.5 bg-cyan-500/20 border border-cyan-500/50 text-cyan-400 text-[9px] md:text-[10px] uppercase font-bold tracking-widest rounded-sm animate-pulse whitespace-nowrap">
                            SECURE
                        </div>
                    </div>
                    
                    <div className="flex w-full md:w-auto gap-1 md:gap-2 overflow-x-auto scrollbar-hide pb-1 md:pb-0">
                        {(["STATUS", "ASK ME", "PROFIL"] as Tab[]).map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`px-3 md:px-4 py-2 text-[10px] md:text-xs font-bold tracking-widest uppercase transition-all border-b-2 flex-1 md:flex-none whitespace-nowrap ${
                                    activeTab === tab 
                                    ? "bg-slate-700 text-white border-cyan-400" 
                                    : "text-slate-400 border-transparent hover:text-cyan-200 hover:bg-slate-800"
                                }`}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="flex-1 overflow-hidden relative bg-[#020617] md:bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]">
                    <AnimatePresence mode="wait">
                        
                        {activeTab === "STATUS" && (
                            <motion.div 
                                key="STATUS" 
                                initial={{ opacity: 0 }} 
                                animate={{ opacity: 1 }} 
                                exit={{ opacity: 0 }} 
                                className="h-full w-full flex items-center justify-center bg-black overflow-hidden relative"
                            >
{/*                               {!videoReady && ( 
                                    <div className="absolute inset-0 z-30 flex flex-col items-center justify-center">
                                        <div className="w-full max-w-xs px-6 flex flex-col items-center">
                                            <motion.div 
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 0.15 }}
                                                className="absolute text-[10rem] font-black italic text-cyan-500 pointer-events-none select-none -z-10"
                                            >
                                                {downloadProgress}
                                            </motion.div>

                                            <div className="text-[10px] font-bold tracking-[0.4em] text-cyan-400 mb-4 animate-pulse uppercase">
                                                Initializing . . .
                                            </div>

                                            <div className="w-full h-[3px] bg-slate-900 border border-slate-800 rounded-full overflow-hidden shadow-[0_0_15px_rgba(6,182,212,0.1)]">
                                                <motion.div 
                                                    className="h-full bg-gradient-to-r from-cyan-600 via-cyan-400 to-white shadow-[0_0_10px_#06b6d4]"
                                                    initial={{ width: 0 }}
                                                    animate={{ width: `${downloadProgress}%` }}
                                                    transition={{ ease: "easeOut" }}
                                                />
                                            </div>

                                            <div className="mt-4 flex justify-between w-full font-mono text-[8px] text-slate-500 tracking-widest uppercase">
                                                <span>Progress Assets</span>
                                                <span>{downloadProgress}%</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
*/}
                                <div className={`w-full h-full flex items-center justify-center ${!videoReady ? 'opacity-0' : 'opacity-100 transition-opacity duration-1000'}`}>
                                    <video 
                                        autoPlay 
                                        loop 
                                        muted={isMuted}
                                        playsInline 
                                        className="w-full h-full object-contain z-0"
                                    >
                                        <source src="/Intro.mp4" type="video/mp4" />
                                    </video>
                                    <div className="absolute top-0 left-0 w-full p-4 flex justify-between items-start z-20 bg-gradient-to-b from-black/80 to-transparent">
                                        <div className="flex flex-col gap-1">
                                            <div className="bg-cyan-500 text-black px-2 py-0.5 text-[9px] font-black uppercase tracking-tighter w-max">
                                                SYSTEM K31
                                            </div>
                                            <div className="text-cyan-500/50 text-[8px] font-mono animate-pulse">
                                                {isMuted ? "AUDIO_MUTED" : "AUDIO_ACTIVE"}
                                            </div>
                                        </div>

                                        <button 
                                            onClick={() => setIsMuted(!isMuted)}
                                            className={`flex items-center gap-2 px-3 py-1.5 border-2 transition-all font-bold text-[10px] tracking-widest ${
                                                isMuted 
                                                ? "border-red-500 text-red-500 bg-red-500/10 hover:bg-red-500 hover:text-white" 
                                                : "border-cyan-500 text-cyan-400 bg-cyan-500/10 hover:bg-cyan-500 hover:text-black"
                                            }`}
                                        >
                                            {isMuted ? "🔇" : "🔊"}
                                            <span className="w-2 h-2 rounded-full bg-current animate-ping" />
                                        </button>
                                    </div>
                                </div>
                                <div className="absolute inset-0 pointer-events-none bg-[linear-gradient(rgba(18,16,16,0)_50%,rgba(0,0,0,0.1)_50%)] bg-[length:100%_4px] opacity-20 z-10" />
                            </motion.div>
                        )}

                        {activeTab === "ASK ME" && (
                            <motion.div key="ASK ME" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col overflow-hidden relative w-[95%] max-w-md mx-auto h-[70vh] border border-slate-800 rounded-lg mt-4 md:absolute md:inset-0 md:w-full md:max-w-none md:h-full md:border-none md:rounded-none md:mt-0">
                                <div className="bg-[#1e293b] border-b border-slate-800 px-3 md:px-4 py-2 flex items-center gap-2 md:gap-3 shrink-0">
                                    <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-cyan-400 animate-pulse" />
                                    <div className="text-[10px] md:text-xs font-bold text-cyan-400 tracking-[0.2em] uppercase">Small Phone</div>
                                </div>
                    
                                <div ref={chatRef} className="flex-1 overflow-y-auto px-4 md:px-6 py-4 md:py-5 space-y-4 md:space-y-6 scrollbar-hide pb-32 md:pb-5">
                                    {messages.map((m) => (
                                        <motion.div key={m.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className={`flex ${m.sender === "user" ? "justify-end" : "justify-start"}`}>
                                            <div className={`max-w-[85%] md:max-w-[80%] px-3 md:px-4 py-2 md:py-3 rounded-md text-xs md:text-[13px] leading-relaxed border ${
                                                m.sender === "user"
                                                ? "bg-slate-800 text-white border-slate-600 rounded-br-none"
                                                : "bg-[#1e293b] text-slate-300 border-cyan-500/30 rounded-bl-none border-l-4 border-l-cyan-500"
                                            }`}>
                                                <div className="text-[8px] md:text-[9px] text-slate-500 mb-1 tracking-widest uppercase">
                                                    {m.sender === "user" ? "Visitor" : "Chill Guy"}
                                                </div>
                                                {m.text}
                                            </div>
                                        </motion.div>
                                    ))}
                    
                                    {thinking && (
                                        <div className="flex justify-start">
                                            <div className="bg-[#1e293b] border border-cyan-600/30 text-cyan-400/70 px-3 md:px-4 py-1.5 md:py-2 rounded-md text-[10px] md:text-[11px] italic flex items-center gap-2">
                                                <span className="w-1 h-1 md:w-1.5 md:h-1.5 bg-cyan-400 rounded-full animate-ping" />
                                                    Now typing...
                                            </div>
                                        </div>
                                    )}
                                </div>
                    
                                <div className="p-3 md:p-4 bg-[#0f172a] border-t border-slate-800 shrink-0">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 md:gap-2 mb-2 md:mb-3">
                                        <AnimatePresence mode="wait">
                                            {!thinking && !isTyping && (
                                                <motion.div 
                                                    initial={{ opacity: 0 }} 
                                                    animate={{ opacity: 1 }} 
                                                    exit={{ opacity: 0 }}
                                                    className="contents"
                                                >
                                                    {STAGE_QUESTIONS[stage].map((q, i) => (
                                                        <button
                                                            key={i}
                                                            disabled={answeredQuestions.includes(q)}
                                                            onClick={() => sendMessage(q)}
                                                            className={`px-3 py-2 border rounded-md text-[10px] font-bold transition-all text-left

                                                                ${
                                                                    answeredQuestions.includes(q)
                                                                        ? "bg-slate-900 border-slate-700 text-slate-500 opacity-50 cursor-not-allowed"
                                                                        : "bg-[#1e293b] border-slate-700 text-slate-300 hover:bg-slate-800 hover:border-cyan-500"
                                                                }
                                                            `}
                                                        >
                                                            {answeredQuestions.includes(q) ? "✓ " : "❓ "}
                                                            {q}
                                                        </button>
                                                    ))}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                    <div className="flex gap-1.5 md:gap-2 h-10 md:h-12">
                                        <input
                                            value={input}
                                            onChange={(e) => setInput(e.target.value)}
                                            onKeyDown={(e) => e.key === "Enter" && sendMessage(input)}
                                            placeholder="ASK ME MSG..."
                                            className="flex-1 bg-[#020617] text-xs md:text-sm text-white placeholder-slate-700 px-3 md:px-4 border-2 border-slate-800 focus:outline-none focus:border-cyan-500 transition-colors"
                                        />
                                        <button
                                            onClick={() => sendMessage(input)}
                                            className="bg-cyan-600 hover:bg-cyan-500 text-white text-[10px] md:text-xs font-black uppercase px-4 md:px-8 transition-colors border-2 border-cyan-700 hover:border-cyan-400"
                                        >
                                            SEND
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        )}

                        {activeTab === "PROFIL" && (
                            <motion.div 
                                key="PROFIL" 
                                initial={{ opacity: 0 }} 
                                animate={{ opacity: 1 }} 
                                exit={{ opacity: 0 }} 
                                className="absolute inset-0 flex items-center justify-center overflow-hidden"
                            >
                                <div className="w-[90%] max-w-md md:max-w-lg bg-[#0f172a] border border-slate-800 p-6 md:p-8 relative overflow-hidden shadow-2xl">
                                    <div className="absolute top-0 right-0 text-[100px] md:text-[150px] font-black text-slate-800/20 leading-none pointer-events-none -mt-4 md:-mt-8 -mr-2 md:-mr-4 italic">31</div>
                                    
                                    <div className="flex justify-between items-start mb-6 md:mb-8 relative z-10">
                                        <div>
                                            <div className="text-slate-500 text-[10px] md:text-xs font-bold tracking-widest mb-1">ID</div>
                                            <div className="text-2xl md:text-4xl font-black text-white italic tracking-tighter">31</div>
                                        </div>
                                        <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-slate-800 border-2 border-cyan-500 flex items-center justify-center shadow-[0_0_15px_rgba(6,182,212,0.4)]">
                                            <div className="text-lg md:text-2xl font-black text-white italic">K</div>
                                        </div>
                                    </div>
                                    <div className="space-y-3 md:space-y-4 relative z-10">
                                        <div className="flex flex-col border-b border-transparent pb-2">
                                            <div className="flex justify-between border-b border-slate-800 pb-2">
                                                <span className="text-slate-500 text-xs md:text-sm">ESTABLISHED</span>
                                                <span className="text-white text-xs md:text-sm font-bold tracking-widest text-right uppercase">JAKARTA, JANUARY 2025</span>
                                            </div>
                                            <span className="text-cyan-500/60 text-[9px] md:text-[10px] font-mono text-right mt-1">
                                                6.2098° S, 106.8001° E
                                            </span>
                                        </div>
                                    </div>
                                    <div className=" relative z-10">
                                        <h4 className="text-cyan-400 text-[10px] uppercase tracking-[0.3em] mb-3">
                                            Connect
                                        </h4>

                                        <div className="flex justify-center gap-6 text-2xl">
                                            <a
                                                href="https://www.youtube.com/@kdl.srn13"
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-slate-400 hover:text-red-500 transition-all"
                                            >
                                                <FaYoutube />
                                            </a>

                                            <a
                                                href="https://www.instagram.com/fcbarcelona/?hl=en"
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-slate-400 hover:text-pink-400 transition-all"
                                            >
                                                <FaInstagram />
                                            </a>

                                            <a
                                                href="https://discord.gg/qZ9jueAv5"
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-slate-400 hover:text-white transition-all"
                                            >
                                                <FaDiscord />
                                            </a>
                                        </div>
                                        <div className="mt-6 relative z-10">
                                            <div className="bg-cyan-500/5 border border-cyan-500/20 rounded-xl p-4 text-center">
                                                <p className="italic text-cyan-300 text-sm">
                                                    "Learn, Yearn, Earn."
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-6 md:mt-8 pt-4 md:pt-6 border-t border-transparent relative z-10">
                                        <div className="text-center text-[10px] md:text-xs text-slate-600 tracking-widest"> K-2026 </div>
                                    </div>
                                </div>
                            </motion.div>
                        )}

                    </AnimatePresence>
                </div>
            </div>
        </div>
    );
};

export default F1Dashboard;